interface IndexedDbConfig {
  databaseName: string;
  version: number;
  store: {
    name: string;
    keyPath: string;
  };
}

const performDbOperation = async <T>(
  config: IndexedDbConfig,
  mode: IDBTransactionMode,
  operation: (objectStore: IDBObjectStore) => IDBRequest<T>
): Promise<T> => {
  return new Promise((resolve, reject) => {
    const storeName = config.store.name;
    const openRequest = indexedDB.open(config.databaseName, config.version);

    openRequest.onerror = () => {
      reject(new Error(`Failed to open database: ${openRequest.error?.message}`));
    };

    openRequest.onsuccess = () => {
      const db = openRequest.result;

      if (!db.objectStoreNames.contains(storeName)) {
        db.close();
        reject(new Error(`Object store '${storeName}' not found`));
        return;
      }

      try {
        const transaction = db.transaction([storeName], mode);
        const objectStore = transaction.objectStore(storeName);
        const request = operation(objectStore);

        request.onsuccess = () => {
          db.close();
          resolve(request.result);
        };

        request.onerror = () => {
          db.close();
          reject(new Error(`Operation failed: ${request.error?.message}`));
        };
      } catch (error) {
        db.close();
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    };

    openRequest.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(config.store.name)) {
        db.createObjectStore(config.store.name, {
          keyPath: config.store.keyPath
        });
      }
    };
  });
};

const insertRecord = async <T>(
  config: IndexedDbConfig,
  record: T
): Promise<IDBValidKey> => {
  return performDbOperation(config, 'readwrite', (store) => store.add(record));
};

const findRecord = async <T>(
  config: IndexedDbConfig,
  key: IDBValidKey
): Promise<T | undefined> => {
  return performDbOperation(config, 'readonly', (store) => store.get(key));
};

const updateRecord = async <T>(
  config: IndexedDbConfig,
  record: T
): Promise<IDBValidKey> => {
  return performDbOperation(config, 'readwrite', (store) => store.put(record));
};

const countRecordsByField = async <T>(
  config: IndexedDbConfig,
  fieldName: keyof T,
  fieldValue: any
): Promise<number> => {
  const allRecords = await performDbOperation<T[]>(
    config,
    'readonly',
    (store) => store.getAll()
  );

  return allRecords?.filter(record => record[fieldName] === fieldValue).length ?? 0;
};

export default {
  insertRecord,
  findRecord,
  updateRecord,
  countRecordsByField,
};

// eslint-disable-next-line prettier/prettier
export type { IndexedDbConfig };
